import { FC, useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { Carousel } from 'dibs-carousel';
import { SbSharedInteriorsHeader } from '../SbSharedInteriorsModule/SbSharedInteriorsHeader';
import { usePhotosLikeDataRefetch } from 'dibs-interiors-photos/exports/usePhotosLikeDataRefetch';

import { SbRespInteriorsLazyLoadImage } from './SbRespInteriorsLazyLoadImage';
import SbSharedInteriorsHeartWrapper from '../SbSharedInteriorsModule/SbSharedInteriorsHeartWrapper/SbSharedInteriorsHeartWrapper';
import SbSharedInteriorsPhotoRelatedSearchLinks from '../SbSharedInteriorsModule/SbSharedInteriorsPhotoRelatedSearchLinks';

import { SbRespInteriorsPhotoCarousel_interiorPhotos } from './__generated__/SbRespInteriorsPhotoCarousel_interiorPhotos.graphql';
import { SbRespInteriorsPhotoCarousel_photos } from './__generated__/SbRespInteriorsPhotoCarousel_photos.graphql';
import { SbRespInteriorsPhotoCarousel_itemSearch } from './__generated__/SbRespInteriorsPhotoCarousel_itemSearch.graphql';
import { SbRespInteriorsPhotoCarousel_user } from './__generated__/SbRespInteriorsPhotoCarousel_user.graphql';

import styles from './SbRespInteriorsPhotoCarousel.scss';

const ITEMS_PER_PAGE = 4;

type ImageLinkProps = {
    index: number;
    onClick: () => void;
};

const ImageLink: FC<ImageLinkProps> = ({ children, index, onClick }) => (
    <div
        tabIndex={0}
        role="link"
        className={styles.imgLink}
        data-tn={`interior-photo-${index}`}
        onClick={onClick}
        onKeyPress={onClick}
    >
        {children}
    </div>
);

type Props = {
    interiorPhotos: SbRespInteriorsPhotoCarousel_interiorPhotos | null;
    photos: SbRespInteriorsPhotoCarousel_photos;
    itemSearch: SbRespInteriorsPhotoCarousel_itemSearch;
    onPhotoIndexChange: (newIndex: number) => void;
    onViewInteriorClick: () => void;
    onPhotoClick: () => void;
    isClient: boolean;
    onPhotoLikeAdded: () => void;
    user: SbRespInteriorsPhotoCarousel_user;
    isModuleVisible: boolean;
};

const SbRespInteriorsPhotoCarouselComponent: FC<Props> = ({
    interiorPhotos,
    photos,
    itemSearch,
    onPhotoIndexChange,
    onViewInteriorClick,
    onPhotoClick,
    isClient,
    onPhotoLikeAdded,
    user,
    isModuleVisible,
}) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const photoCount = photos.length;
    const { photoLikeDataMap, isRefetching } = usePhotosLikeDataRefetch({
        photosRef: photos,
        shouldFetch: isModuleVisible,
        userId: user?.serviceId || '',
    });
    return (
        <div className={styles.wrapper}>
            <div className={styles.headerWrapper}>
                <SbSharedInteriorsHeader itemSearch={itemSearch} />
            </div>
            <div className={styles.imgWrapper}>
                {photoCount < ITEMS_PER_PAGE ? (
                    <div className={styles.centeredImgWrapper}>
                        {photos.map((photo, index) => {
                            const { photoLikeId } =
                                photoLikeDataMap.get(photo?.serviceId || '') || {};
                            return (
                                <ImageLink
                                    key={photo.path || index}
                                    index={index}
                                    onClick={onPhotoClick}
                                >
                                    <SbRespInteriorsLazyLoadImage
                                        imgClass={styles.img}
                                        lazyLoadClass={styles.lazyLoad}
                                        photo={photo}
                                    />
                                    {isClient && !isRefetching && (
                                        <div className={styles.heartWrapper}>
                                            <SbSharedInteriorsHeartWrapper
                                                onPhotoLikeAdded={onPhotoLikeAdded}
                                                photoLikeId={photoLikeId}
                                                photo={photo}
                                            />
                                        </div>
                                    )}
                                </ImageLink>
                            );
                        })}
                    </div>
                ) : (
                    <Carousel
                        classNames={{
                            listWrapper: imageLoaded ? styles.carouselGradient : undefined,
                            item: styles.carouselItem,
                        }}
                        dataTn="sb-interiors-photo-carousel"
                        totalItems={photos.length}
                        isInfinite={false}
                        slideCarouselItems
                        itemsPerPage={ITEMS_PER_PAGE}
                        step={ITEMS_PER_PAGE}
                        onIndexChange={({ index }: { index: number }) => onPhotoIndexChange(index)}
                        renderItem={({ index }: { index: number; isCurrentItem: boolean }) => {
                            const currentPhoto = photos[index];
                            const { photoLikeId } =
                                photoLikeDataMap.get(currentPhoto?.serviceId || '') || {};
                            return (
                                <>
                                    <ImageLink index={index} onClick={onPhotoClick}>
                                        <SbRespInteriorsLazyLoadImage
                                            imgClass={styles.carouselImg}
                                            lazyLoadClass={styles.carouselLazyLoad}
                                            photo={currentPhoto}
                                            onImageLoaded={() => setImageLoaded(true)}
                                        />
                                        {imageLoaded && (
                                            <div
                                                className={styles.overlay}
                                                onClick={onViewInteriorClick}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={event => {
                                                    if (event.key === 'Enter') {
                                                        onViewInteriorClick();
                                                    }
                                                }}
                                            >
                                                {isClient && !isRefetching && (
                                                    <SbSharedInteriorsHeartWrapper
                                                        onPhotoLikeAdded={onPhotoLikeAdded}
                                                        photoLikeId={photoLikeId}
                                                        photo={currentPhoto}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </ImageLink>
                                </>
                            );
                        }}
                    />
                )}
            </div>
            <div className={styles.relatedSearchContainer}>
                <SbSharedInteriorsPhotoRelatedSearchLinks
                    isPillBackgroundWhite
                    interiorPhotos={interiorPhotos}
                />
            </div>
        </div>
    );
};

export const SbRespInteriorsPhotoCarousel = createFragmentContainer(
    SbRespInteriorsPhotoCarouselComponent,
    {
        itemSearch: graphql`
            fragment SbRespInteriorsPhotoCarousel_itemSearch on ItemSearchQueryConnection {
                ...SbSharedInteriorsHeader_itemSearch
            }
        `,
        interiorPhotos: graphql`
            fragment SbRespInteriorsPhotoCarousel_interiorPhotos on SearchBrowseInteriorPhotosModule {
                ...SbSharedInteriorsPhotoRelatedSearchLinks_interiorPhotos
            }
        `,
        photos: graphql`
            fragment SbRespInteriorsPhotoCarousel_photos on PhotosSearchBrowsePhoto
            @relay(plural: true) {
                id
                serviceId
                path
                name
                ...usePhotosLikeDataRefetch_photos
                ...SbSharedInteriorsHeartWrapper_photo
            }
        `,
        user: graphql`
            fragment SbRespInteriorsPhotoCarousel_user on User {
                serviceId
            }
        `,
    }
);
